import React, { useState, useRef, useEffect } from "react";
import useLogout from '../hooks/useLogout';
import useAuth from "../hooks/useAuth";
import { Menu, MenuList, MenuItem, IconButton, ListItemText, ListItemIcon } from '@mui/material';
import { Link, useLocation, useNavigate } from "react-router-dom";
import LanguageSwitcher from "./LanguageSwitcher";
import { useTranslation } from 'react-i18next';
import NotificationService from "../services/notification.service";
import useApi from "../hooks/useApi";

const MenuHeader = () => {
  const location = useLocation();
  const [isSubMenuOpen, setIsSubMenuOpen] = useState(false);
  const navigate = useNavigate();
  const logout = useLogout();
  const { auth } = useAuth();
  const [user, setUser] = useState();
  const [unreadCount, setUnreadCount] = useState(0);
  const timeoutRef = useRef(null);
  const api = useApi();
  const signOut = async () => {
    await logout();
    navigate('/login');
  }

  const { t } = useTranslation('menu');

  const handleCustomersClick = (event) => {
    handleClose();
    if (location.pathname === '/customer') {
      event.preventDefault();
      window.location.assign('/customer');
    }
  };

  const getMenuItem = () => {
    const queryParams = new URLSearchParams(location.search);
    const tab = queryParams.get('tab');

    switch (location.pathname) {
      case '/':
        return t('header.dashboard');
      case '/customer':
        return t('header.customers');
      case '/flypro':
        return t('header.flypro');
      case '/calendar':
        return t('header.calendarManagement');
      case '/managers':
        return t('header.managers');
      case '/products':
        return t('header.domainAndProducts');
      case '/manager-products':
        return t('header.products');
      case '/hotels':
        return t('header.hotelManagement');
      case '/notification':
        return t('header.notification');
      case '/promotion':
        return t('header.promotion');
      case '/gift':
        return t('header.gift');
      case '/statistics':
        return t('header.statistics');
      case '/rbac-management':
        return 'Role Based Access Control';
      case '/Management':
        return t('header.settings');
      case '/management?tab=unavailable-zones':
        return t('header.unavailableZones');
      case '/management?tab=zone-management':
        return t('header.zoneManagement');
      case '/management':
        if (tab === 'zone-management') {
          return t('header.zoneManagement');
        }
        if (tab === 'unavailable-zones') {
          return t('header.unavailableZones');
        }
        return t('header.profile');
      default:
        return 'Home';
    }
  };

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const getUsers = async () => {
      try {
        if (isMounted) {
          setUser(auth?.user);
          setUnreadCount(auth?.user?.notifications?.filter(
            (notification) => notification.is_read !== "1"
          ).length || 0);
        }
      } catch (err) {
        console.error(err);
      }
    };

    if (!auth || Object.keys(auth).length === 0) {
      return;
    }
    getUsers();
    console.log({ auth })
    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [auth]);

  const handleMenuClick = () => {
    setIsSubMenuOpen((prev) => !prev);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsSubMenuOpen(false);
    }, 250);
  };

  const handleNotificationClick = (e) => {
    e.preventDefault(); // Prevent default link behavior
    markNotificationsAsRead();
    navigate('/notification-list');
  };

  const markNotificationsAsRead = async () => {
    try {
      await NotificationService.markAllNotificationsAsRead(api);
      setUnreadCount(0);
    } catch (error) {
      console.error("Error marking notifications as read:", error);
    }
  };
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <div className="menu-header">
      <div className="container">
        <div className="menu logo-container">
          <div className="menu-right">
            <ul>
              <li
                className="parent"
                onMouseLeave={handleMouseLeave}
              >
                <IconButton onClick={handleClick}>
                  <img src='/assets/images/icon-burger.svg' alt='burger' width={16} height={20} />
                </IconButton>
                {auth?.permissions?.includes('admin.access') && (
                  <>
                    <Menu anchorEl={anchorEl}
                      open={open}
                      onClose={handleClose}
                    >
                      <MenuList
                        style={{ minWidth: 200 }}
                      >
                        {['bookings-manage', 'bookings-read'].every(perm => auth?.permissions?.includes(perm)) &&
                          (
                            <Link to="/" style={{ color: '#393D44' }}>
                              <MenuItem onClick={handleClose}>
                                <ListItemText>{t('header.dashboard')}</ListItemText>
                              </MenuItem>
                            </Link>
                          )
                        }
                        {['users-manage.ROLE_CLIENT', 'users-read'].every(perm => auth?.permissions?.includes(perm)) &&
                          (
                            <Link to="/customer" style={{ color: '#393D44' }}>
                              <MenuItem onClick={handleCustomersClick}>
                                <ListItemText>{t('header.customers')}</ListItemText>
                              </MenuItem>
                            </Link>
                          )
                        }
                        {['users-manage.ROLE_FLYPRO', 'users-read'].every(perm => auth?.permissions?.includes(perm)) &&
                          (
                            <Link to="/flypro" style={{ color: '#393D44' }}>
                              <MenuItem onClick={handleClose}>
                                <ListItemText>{t('header.flypro')}</ListItemText>
                              </MenuItem>
                            </Link>
                          )
                        }
                        {['calendars-manage', 'calendars-read'].every(perm => auth?.permissions?.includes(perm)) &&
                          (
                            <Link to="/calendar" style={{ color: '#393D44' }}>
                              <MenuItem onClick={handleClose}>
                                <ListItemText>{t('header.calendarManagement')}</ListItemText>
                              </MenuItem>
                            </Link>
                          )
                        }
                        {['users-read'].every(perm => auth?.permissions?.includes(perm)) &&
                          (
                            <Link to="/managers" style={{ color: '#393D44' }}>
                              <MenuItem onClick={handleClose}>
                                <ListItemText>{t('header.managers')}</ListItemText>
                              </MenuItem>
                            </Link>
                          )
                        }
                        {
                          (
                            ['products-manage', 'products-read'].every(perm => auth?.permissions?.includes(perm)) ||
                            ['domains-manage', 'domains-read'].every(perm => auth?.permissions?.includes(perm)) ||
                            ['categories-manage', 'categories-read'].every(perm => auth?.permissions?.includes(perm))
                          ) &&
                          (
                            <Link to="/products" style={{ color: '#393D44' }}>
                              <MenuItem onClick={handleClose}>
                                <ListItemText>{t('header.domainAndProducts')}</ListItemText>
                              </MenuItem>
                            </Link>
                          )
                        }
                        {['hotels-manage', 'hotels-read'].every(perm => auth?.permissions?.includes(perm)) &&
                          (
                            <Link to="/hotels" style={{ color: '#393D44' }}>
                              <MenuItem onClick={handleClose}>
                                <ListItemText>{t('header.hotelManagement')}</ListItemText>
                              </MenuItem>
                            </Link>
                          )
                        }
                        {['notifications-read'].every(perm => auth?.permissions?.includes(perm)) &&
                          (
                            <Link to="/notification" style={{ color: '#393D44' }}>
                              <MenuItem onClick={handleClose}>
                                <ListItemText>{t('header.notification')}</ListItemText>
                              </MenuItem>
                            </Link>
                          )
                        }
                        {['promotions-manage', 'promotions-read'].every(perm => auth?.permissions?.includes(perm)) &&
                          (
                            <Link to="/promotion" style={{ color: '#393D44' }}>
                              <MenuItem onClick={handleClose}>
                                <ListItemText>{t('header.promotion')}</ListItemText>
                              </MenuItem>
                            </Link>
                          )
                        }
                        {['gift_configurations-manage', 'gift_configurations-read'].every(perm => auth?.permissions?.includes(perm)) &&
                          (
                            <Link to="/gift" style={{ color: '#393D44' }}>
                              <MenuItem onClick={handleClose}>
                                <ListItemText>{t('header.gift')}</ListItemText>
                              </MenuItem>
                            </Link>
                          )
                        }
                        {['unavailable_zones-manage', 'unavailable_zones-read'].every(perm => auth?.permissions?.includes(perm)) &&
                          (
                            <Link to="/management?tab=unavailable-zones" style={{ color: '#393D44' }}>
                              <MenuItem onClick={handleClose}>
                                <ListItemText>{t('header.unavailableZones')}</ListItemText>
                              </MenuItem>
                            </Link>
                          )
                        }
                        {['zones-manage', 'zones-read'].every(perm => auth?.permissions?.includes(perm)) &&
                          (
                            <Link to="/management?tab=zone-management" style={{ color: '#393D44' }}>
                              <MenuItem onClick={handleClose}>
                                <ListItemText>{t('header.zoneManagement')}</ListItemText>
                              </MenuItem>
                            </Link>
                          )
                        }
                        {['bookings-manage', 'bookings-read'].every(perm => auth?.permissions?.includes(perm)) &&
                          (
                            <Link to="/statistics" style={{ color: '#393D44' }}>
                              <MenuItem onClick={handleClose}>
                                <ListItemText>{t('header.statistics')}</ListItemText>
                              </MenuItem>
                            </Link>
                          )
                        }
                        {['roles-manage', 'permissions-manage', 'roles-read', 'permissions-read'].every(perm => auth?.permissions?.includes(perm)) &&
                          (
                            <Link to="/rbac-management" style={{ color: '#393D44' }}>
                              <MenuItem onClick={handleClose}>
                                <ListItemText>Role Based Access Control</ListItemText>
                              </MenuItem>
                            </Link>
                          )
                        }
                        {[].every(perm => auth?.permissions?.includes(perm)) &&
                          (
                            <Link to="/Management?tab=profile" style={{ color: '#393D44' }}>
                              <MenuItem onClick={handleClose}>
                                <ListItemText>{t('header.settings')}</ListItemText>
                              </MenuItem>
                            </Link>
                          )
                        }
                        <MenuItem onClick={signOut}>
                          <ListItemText>{t('header.logout')}</ListItemText>
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  </>
                )}

                {auth?.roles?.some(role => role.name === 'ROLE_HOTEL_MANAGER') && (
                  <>
                    <Menu anchorEl={anchorEl}
                      open={open}
                      onClose={handleClose}
                    >
                      <MenuList
                        style={{ minWidth: 200 }}
                      >
                        <Link to="/tasks" style={{ color: '#393D44' }}>
                          <MenuItem onClick={handleClose}>
                            <ListItemText>{t('header.ourBookings')}</ListItemText>
                          </MenuItem>
                        </Link>
                        <Link to="/choose-products" style={{ color: '#393D44' }}>
                          <MenuItem onClick={handleClose}>
                            <ListItemText>{t('header.addBooking')}</ListItemText>
                          </MenuItem>
                        </Link>
                        <Link to="/manager-products" style={{ color: '#393D44' }}>
                          <MenuItem onClick={handleClose}>
                            <ListItemText>{t('header.products')}</ListItemText>
                          </MenuItem>
                        </Link>
                        <Link to="/management?tab=profile" style={{ color: '#393D44' }}>
                          <MenuItem onClick={handleClose}>
                            <ListItemText>{t('header.myProfile')}</ListItemText>
                          </MenuItem>
                        </Link>
                        {/* <Link to="/management?tab=hotel-availability" style={{ color: '#393D44' }}>
                          <MenuItem onClick={handleClose}>
                            <ListItemText>{t('header.hotelAvailability')}</ListItemText>
                          </MenuItem>
                        </Link> */}
                        <MenuItem onClick={signOut}>
                          <ListItemText>{t('header.logout')}</ListItemText>
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  </>
                )}
              </li>
            </ul>
          </div>
          {auth?.roles?.some(role => role.name !== 'ROLE_HOTEL_MANAGER') && (
            <a href="/" className="logo">FLYSPA</a>
          )}
          {auth?.roles?.some(role => role.name === 'ROLE_HOTEL_MANAGER') && (
            <a href="/tasks" className="logo">FLYSPA</a>
          )}
        </div>
        <div className="menu">
          <div className="menu-left">
            <ul>
              <li>
                {getMenuItem() === 'Home' ? (
                  <a href="/">{t('header.home')}</a>
                ) : (
                  <a href="#">{getMenuItem()}</a>
                )}
              </li>
            </ul>
          </div>
          <div className="menu-right">
            <ul>
              <li style={{ display: 'flex', alignItems: 'center', marginRight: '15px' }}>
                <LanguageSwitcher />
              </li>
              <li>
                <a className="notif" onClick={handleNotificationClick}>
                  <span>{unreadCount}</span>
                </a>
              </li>
              <li>
                <a href="/management" className="account">{user?.extra?.initials}</a>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MenuHeader;
